import axios, { AxiosError } from 'axios';

const AUTH_API_URL = process.env.REACT_APP_AUTH_API_URL;
const DCMM_API_URL = process.env.REACT_APP_DCMM_API_URL;
const IS_ON_PREM = process.env.REACT_APP_DEPLOYMENT_MODE === 'on-prem';

export interface DeviceCredentials {
  apiKey: string;
  secretKey: string;
  status: string;
}

export interface DeviceInfo {
  _id: string;
  ownerType: 'AIRPORT' | 'CLIENT';
  ownerId: string;
  deviceCode: string;
  macAddress: string;
  status: string;
  isOnline: boolean;
  isConfigured: boolean;
}

export interface RegisterResponse {
  statusCode: number;
  message: string;
  success: boolean;
  data: {
    device: DeviceInfo;
    credentials: DeviceCredentials;
  };
}

export interface LoginResponse {
  statusCode: number;
  message: string;
  success: boolean;
  data: {
    token: string;
    expiresIn: number;
  };
}

export interface StoredCredentials extends DeviceCredentials {
  ownerType: 'AIRPORT' | 'CLIENT';
  deviceId: string;
  deviceCode: string;
  tokenExpiresAt?: number;
}

export interface RetryConfig {
  maxRetries?: number;
  initialDelay?: number;
  maxDelay?: number;
  backoffMultiplier?: number;
  retryableStatuses?: number[];
}

const STORAGE_KEY = 'fids_device_credentials';
const TOKEN_KEY = 'fids_auth_token';
const POS_AVIATION_KEY = 'fids_pos_aviation';

const DEFAULT_RETRY_CONFIG: Required<RetryConfig> = {
  maxRetries: 20,
  initialDelay: 1000,
  maxDelay: 10000,
  backoffMultiplier: 2,
  retryableStatuses: [408, 429, 500, 502, 503, 504],
};

const sleep = (ms: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms));

// Reads the `exp` claim (ms) so expiry follows the server's clock, not the device's.
const getJwtExpiry = (token: string): number | null => {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload));
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
};

// Shared by every caller that needs a fresh token, so a burst of 401s
// triggers one login instead of one per request.
let loginInFlight: Promise<{ token: string; expiresAt: number }> | null = null;

const isRetryableError = (error: AxiosError, retryableStatuses: number[]): boolean => {
  if (!error.response) return true;
  return retryableStatuses.includes(error.response.status);
};

const calculateDelay = (
  attemptNumber: number,
  initialDelay: number,
  maxDelay: number,
  backoffMultiplier: number,
): number => Math.min(initialDelay * Math.pow(backoffMultiplier, attemptNumber), maxDelay);

async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  config: RetryConfig = {},
  context?: string,
): Promise<T> {
  const { maxRetries, initialDelay, maxDelay, backoffMultiplier, retryableStatuses } = {
    ...DEFAULT_RETRY_CONFIG,
    ...config,
  };

  let lastError: Error;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;

      const isAxiosError = axios.isAxiosError(error);
      const shouldRetry = isAxiosError && isRetryableError(error, retryableStatuses);
      const isLastAttempt = attempt === maxRetries;

      if (!shouldRetry || isLastAttempt) {
        console.error(
          `${context || 'Request'} failed after ${attempt + 1} attempt(s):`,
          error.response?.data || error.message,
        );
        throw error;
      }

      const delay = calculateDelay(attempt, initialDelay, maxDelay, backoffMultiplier);
      console.warn(
        `${context || 'Request'} failed (attempt ${attempt + 1}/${maxRetries + 1}). Retrying in ${delay}ms...`,
        error.response?.status,
        error.response?.data?.message || error.message,
      );

      await sleep(delay);
    }
  }

  throw lastError!;
}

export class DeviceConfigService {

  // ── Registration ──────────────────────────────────────────────────────────────
  // Cloud only — on-prem devices are provisioned when DisplayLayout is created

  static async registerClient(
    locationCode: string,
    email: string,
    retryConfig?: RetryConfig,
  ): Promise<RegisterResponse> {
    return retryWithBackoff(
      async () => {
        try {
          const response = await axios.post<RegisterResponse>(
            `${DCMM_API_URL}/devices/client/register`,
            { locationCode, email },
            { headers: { 'Content-Type': 'application/json' }, timeout: 30000 },
          );
          if (response.data.success) {
            this.saveCredentials({
              apiKey: response.data.data.credentials.apiKey,
              secretKey: response.data.data.credentials.secretKey,
              status: response.data.data.credentials.status,
              ownerType: 'CLIENT',
              deviceId: response.data.data.device._id,
              deviceCode: response.data.data.device.deviceCode,
            });
          }
          return response.data;
        } catch (error: any) {
          const message = error.response?.data?.message || 'Failed to register device as CLIENT';
          const enhanced: any = new Error(message);
          enhanced.response = error.response;
          enhanced.status = error.response?.status;
          // Keep the axios shape so retryWithBackoff still retries network/5xx failures
          enhanced.isAxiosError = axios.isAxiosError(error);
          throw enhanced;
        }
      },
      retryConfig,
      'Register CLIENT device',
    );
  }

  static async registerAirport(
    locationCode: string,
    retryConfig?: RetryConfig,
  ): Promise<RegisterResponse> {
    return retryWithBackoff(
      async () => {
        try {
          const response = await axios.post<RegisterResponse>(
            `${DCMM_API_URL}/devices/register`,
            { locationCode },
            { headers: { 'Content-Type': 'application/json' }, timeout: 30000 },
          );
          if (response.data.success) {
            this.saveCredentials({
              apiKey: response.data.data.credentials.apiKey,
              secretKey: response.data.data.credentials.secretKey,
              status: response.data.data.credentials.status,
              ownerType: 'AIRPORT',
              deviceId: response.data.data.device._id,
              deviceCode: response.data.data.device.deviceCode,
            });
          }
          return response.data;
        } catch (error: any) {
          const message = error.response?.data?.message || 'Failed to register device as AIRPORT';
          const enhanced: any = new Error(message);
          enhanced.response = error.response;
          enhanced.status = error.response?.status;
          // Keep the axios shape so retryWithBackoff still retries network/5xx failures
          enhanced.isAxiosError = axios.isAxiosError(error);
          throw enhanced;
        }
      },
      retryConfig,
      'Register AIRPORT device',
    );
  }

  // ── Login ─────────────────────────────────────────────────────────────────────
  // On-prem → POST /device-auth/loginByIp  (no body — IP extracted by backend)
  // Cloud   → POST /device-auth/login      (apiKey + secretKey from localStorage)
  // Both paths call saveToken() identically — all callers need no branching.

  static async login(
    apiKey?: string,
    secretKey?: string,
    retryConfig?: RetryConfig,
  ): Promise<{ token: string; expiresAt: number }> {
    return retryWithBackoff(
      async () => {
        try {
          let token: string;
          let expiresAt: number;

          if (IS_ON_PREM) {
            // ── On-prem: IP is the credential — backend extracts it from request ──
            // const deviceIp = await getLocalIp();

            // console.log('[loginByIp] resolved device IP:', deviceIp);

            const response = await axios.post(
              `${AUTH_API_URL}/device-auth/loginByIp`,
              // { ip: deviceIp },
              { headers: { 'Content-Type': 'application/json' }, timeout: 30000 },
            );

            // Handle both raw { token } and NestJS-wrapped { data: { token } }
            token = response.data?.data?.token ?? response.data?.token;

            console.log('[loginByIp] response shape:', JSON.stringify(response.data));

            if (!token) {
              throw new Error('No token received from loginByIp');
            }

            // Mirror DEVICE_JWT_EXPIRES_IN (365d) — backend doesn't return expiresIn
            expiresAt = Date.now() + 365 * 24 * 60 * 60 * 1000;

          } else {
            // ── Cloud: use apiKey + secretKey from localStorage ──────────────────
            const credentials = apiKey && secretKey
              ? { apiKey, secretKey }
              : this.getCredentials();

            if (!credentials) {
              throw new Error('No credentials found. Please configure device first.');
            }

            const response = await axios.post<LoginResponse>(
              `${AUTH_API_URL}/device-auth/login`,
              { apiKey: credentials.apiKey, secretKey: credentials.secretKey },
              { headers: { 'Content-Type': 'application/json' }, timeout: 30000 },
            );

            if (!response.data.success) {
              throw new Error(response.data.message || 'Login failed');
            }

            token = response.data.data.token;
            expiresAt = Date.now() + response.data.data.expiresIn * 1000;
          }

          expiresAt = getJwtExpiry(token) ?? expiresAt;
          this.saveToken(token, expiresAt);

          return { token, expiresAt };
        } catch (error: any) {
          const message = error.response?.data?.message || error.message || 'Authentication failed';
          const enhanced: any = new Error(message);
          enhanced.response = error.response;
          enhanced.status = error.response?.status;
          // Keep the axios shape so retryWithBackoff still retries network/5xx failures
          enhanced.isAxiosError = axios.isAxiosError(error);
          throw enhanced;
        }
      },
      retryConfig,
      IS_ON_PREM ? 'Device login (IP)' : 'Device login',
    );
  }

  // ── isConfigured ──────────────────────────────────────────────────────────────
  // On-prem: always true — IP is the identity, no manual config step needed
  // Cloud:   true only after registrationRequest stored credentials

  static isConfigured(): boolean {
    if (IS_ON_PREM) return true;
    return this.getCredentials() !== null;
  }

  // ── Token helpers ─────────────────────────────────────────────────────────────

  static saveToken(token: string, expiresAt: number): void {
    try {
      localStorage.setItem(TOKEN_KEY, JSON.stringify({ token, expiresAt }));
    } catch (error) {
      console.error('Failed to save token:', error);
      try {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.setItem(TOKEN_KEY, JSON.stringify({ token, expiresAt }));
      } catch {
        throw new Error('Unable to save token. Storage might be full or disabled.');
      }
    }
  }

  static getToken(): { token: string; expiresAt: number } | null {
    try {
      // Check localStorage first
      const stored = localStorage.getItem(TOKEN_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          return parsed;
        } catch {
          console.error('[getToken] corrupted token in localStorage — clearing');
          localStorage.removeItem(TOKEN_KEY);
        }
      }

      // Cloud only: backward-compat URL token (on-prem never uses query string tokens)
      if (!IS_ON_PREM) {
        const urlToken = new URLSearchParams(window.location.search).get('token');
        if (urlToken) {
          const expiresAt = Date.now() + 24 * 60 * 60 * 1000;
          try { this.saveToken(urlToken, expiresAt); } catch { }
          return { token: urlToken, expiresAt };
        }
      }

      console.warn('[getToken] no token found');
      return null;
    } catch (error) {
      console.error('[getToken] error:', error);
      return null;
    }
  }

  static isTokenExpired(): boolean {
    const tokenData = this.getToken();
    if (!tokenData) return true;
    const oneHourFromNow = Date.now() + 60 * 60 * 1000;
    return tokenData.expiresAt < oneHourFromNow;
  }

  // Logs in again with the stored credentials. Concurrent callers share one request.
  // Drops an already-expired token first so it is never sent again.
  static reLogin(retryConfig?: RetryConfig): Promise<{ token: string; expiresAt: number }> {
    if (!loginInFlight) {
      const current = this.getToken();
      if (current && current.expiresAt <= Date.now()) {
        try { localStorage.removeItem(TOKEN_KEY); } catch { }
      }
      loginInFlight = this.login(undefined, undefined, retryConfig).finally(() => {
        loginInFlight = null;
      });
    }
    return loginInFlight;
  }

  static async refreshTokenIfNeeded(retryConfig?: RetryConfig): Promise<string | null> {
    if (this.isTokenExpired()) {
      try {
        const { token } = await this.reLogin(retryConfig);
        return token;
      } catch (error) {
        console.error('[refreshTokenIfNeeded] refresh failed:', error);
        // Inside the 1h refresh window the old token still works — keep using it
        const current = this.getToken();
        return current && current.expiresAt > Date.now() ? current.token : null;
      }
    }
    return this.getToken()?.token || null;
  }

  // ── POS-AVIATION helpers ──────────────────────────────────────────────────────
  // Set during client registration. Absent key reads as false.

  static setPosAviation(enabled: boolean): void {
    try {
      localStorage.setItem(POS_AVIATION_KEY, String(enabled));
    } catch (error) {
      console.error('Failed to save POS-AVIATION flag:', error);
    }
  }

  static isPosAviation(): boolean {
    try {
      return localStorage.getItem(POS_AVIATION_KEY) === 'true';
    } catch (error) {
      console.error('Failed to read POS-AVIATION flag:', error);
      return false;
    }
  }

  // ── Credential helpers ────────────────────────────────────────────────────────

  static saveCredentials(credentials: StoredCredentials): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(credentials));
    } catch (error) {
      console.error('Failed to save credentials:', error);
      try {
        localStorage.clear();
        localStorage.setItem(STORAGE_KEY, JSON.stringify(credentials));
      } catch {
        throw new Error('Unable to save credentials. Storage might be full or disabled.');
      }
    }
  }

  static getCredentials(): StoredCredentials | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;
      return JSON.parse(stored);
    } catch (error) {
      console.error('Failed to get credentials:', error);
      try { localStorage.removeItem(STORAGE_KEY); } catch { }
      return null;
    }
  }

  static clearCredentials(): void {
    const errors: Error[] = [];
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { errors.push(e as Error); }
    try { localStorage.removeItem(TOKEN_KEY); } catch (e) { errors.push(e as Error); }
    try { localStorage.removeItem(POS_AVIATION_KEY); } catch (e) { errors.push(e as Error); }
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('token');
      window.history.replaceState({}, '', url.toString());
    } catch (e) { errors.push(e as Error); }
    if (errors.length > 0) console.warn(`Cleared credentials with ${errors.length} error(s)`);
  }

  static getDeviceInfo(): Pick<StoredCredentials, 'ownerType' | 'deviceId' | 'deviceCode'> | null {
    const credentials = this.getCredentials();
    if (!credentials) return null;
    return {
      ownerType: credentials.ownerType,
      deviceId: credentials.deviceId,
      deviceCode: credentials.deviceCode,
    };
  }

  // ── Health check ──────────────────────────────────────────────────────────────

  static async healthCheck(retryConfig?: RetryConfig): Promise<{
    authApi: boolean;
    dcmmApi: boolean;
    errors?: string[];
  }> {
    const results = { authApi: false, dcmmApi: false, errors: [] as string[] };

    try {
      await retryWithBackoff(
        async () => { await axios.get(`${AUTH_API_URL}/health`, { timeout: 5000 }); results.authApi = true; },
        { ...retryConfig, maxRetries: 1 },
        'AUTH API health check',
      );
    } catch (e: any) { results.errors.push(`AUTH API: ${e.message}`); }

    try {
      await retryWithBackoff(
        async () => { await axios.get(`${DCMM_API_URL}/health`, { timeout: 5000 }); results.dcmmApi = true; },
        { ...retryConfig, maxRetries: 1 },
        'DCMM API health check',
      );
    } catch (e: any) { results.errors.push(`DCMM API: ${e.message}`); }

    return results;
  }
}

/**
 * Get this device's LAN IP using WebRTC.
 * Works in Chromium on Raspberry Pi — no external service needed.
 * Falls back to REACT_APP_DEVICE_IP env var if WebRTC is unavailable.
 */
const getLocalIp = (): Promise<string> => {
  return new Promise((resolve) => {
    // Env override — useful for dev/testing
    const envIp = process.env.REACT_APP_DEVICE_IP;
    if (envIp) {
      resolve(envIp);
      return;
    }

    try {
      const pc = new RTCPeerConnection({ iceServers: [] });
      const timeout = setTimeout(() => {
        pc.close();
        resolve('unknown');
      }, 3000);

      pc.createDataChannel('');
      pc.createOffer()
        .then(offer => pc.setLocalDescription(offer))
        .catch(() => { clearTimeout(timeout); resolve('unknown'); });

      pc.onicecandidate = (ice) => {
        if (!ice.candidate) return;
        // Extract IPv4 from candidate string
        const match = ice.candidate.candidate.match(
          /(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})/
        );
        if (match && !match[1].startsWith('127.')) {
          clearTimeout(timeout);
          pc.close();
          resolve(match[1]);
        }
      };
    } catch {
      resolve('unknown');
    }
  });
};

export { retryWithBackoff, sleep, DEFAULT_RETRY_CONFIG };